import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import {
	Backdrop,
	Chip,
	CircularProgress,
	Link,
	ListItemText,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Assignment, AssignmentStatus, statusToThemeKey } from "../../api";
import { JobylonLinkedJob } from "../../api/assignments/jobylon-jobs";
import { formatDateTimeWithSeconds } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";
import { JobylonBadge } from "../shared/jobylon-badge";
import MenuActions from "./MenuActions";

export function descendingComparator<
	T extends { status?: keyof typeof AssignmentStatus },
>(a: T, b: T, orderBy: keyof T) {
	let orderA = orderBy === "status" ? AssignmentStatus[a.status] : a[orderBy];
	let orderB = orderBy === "status" ? AssignmentStatus[b.status] : b[orderBy];

	// Add special handling for votes (number, can be undefined)
	if (orderBy === "votes") {
		orderA = typeof a[orderBy] === "number" ? (a[orderBy] as number) : -1;
		orderB = typeof b[orderBy] === "number" ? (b[orderBy] as number) : -1;
	}

	if (orderB < orderA) {
		return -1;
	}
	if (orderB > orderA) {
		return 1;
	}
	return 0;
}

export function getComparator<Key extends keyof any>(
	order: "desc" | "asc",
	orderBy: Key
): (
	a: {
		[key in Key]: any;
	},
	b: {
		[key in Key]: any;
	}
) => number {
	console.debug({ orderBy });
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

const CustomTableCell = ({
	order,
	orderBy,
	property,
	label,
	handleRequestSort,
}: {
	order: "desc" | "asc";
	orderBy: keyof Assignment;
	property: keyof Assignment;
	label: string;
	handleRequestSort: (property: keyof Assignment) => void;
}) => {
	const createSortHandler = () => {
		console.debug({ property });
		handleRequestSort(property);
	};
	return (
		<TableCell>
			<TableSortLabel
				active={orderBy === property}
				direction={orderBy === property ? order : "asc"}
				onClick={() => createSortHandler()}
			>
				{label}
			</TableSortLabel>
		</TableCell>
	);
};

const AssignmentsList = ({
	assignments,
	onClickAssignment,
	onCloneAssignment,
	onDeleteAssignment,
	onGoToBoard,
	id,
	initialOrder = "desc",
	initialOrderBy = "id",
	onSortChange,
	jobylonByAssignment = new Map(),
}: {
	assignments: Assignment[];
	onClickAssignment: (assignment: Assignment) => void;
	onCloneAssignment: (assignment: Assignment) => void;
	onDeleteAssignment: (assignment: Assignment) => void;
	onGoToBoard: (assignment: Assignment) => void;
	/**
	 * Jobylon board URL per assignment id. A role listed here keeps its
	 * candidates in Jobylon, so its row offers that board instead of ODI's.
	 */
	jobylonByAssignment?: Map<number, JobylonLinkedJob>;
	id?: string;
	initialOrder?: "asc" | "desc";
	initialOrderBy?: keyof Assignment;
	onSortChange?: (order: "asc" | "desc", orderBy: keyof Assignment) => void;
}) => {
	const { currentUser } = useGlobalState();
	const theme = useTheme();
	const [sortedAssignments, setSortedAssignments] = useState<Assignment[]>([]);
	const [order, setOrder] = useState<"asc" | "desc">(initialOrder);
	const [orderBy, setOrderBy] = useState<keyof Assignment>(initialOrderBy);
	const [sorting, setSorting] = useState(false);

	// Check if user belongs to a recruiting company
	const isRecruitingCompany =
		currentUser?.userprofile?.up_group?.recruiting_company === true;
	const isAdmin = !!currentUser?.is_superuser;

	useEffect(() => {
		setSorting(true);
		setTimeout(() => {
			setSortedAssignments(
				[...assignments]
					.map((el) => ({ ...el }))
					.sort(getComparator(order, orderBy))
			);
			setSorting(false);
		}, 100); // Simulate async sorting for UX
	}, [assignments, order, orderBy]);

	const handleRequestSort = (property: keyof Assignment) => {
		const isAsc = orderBy === property && order === "asc";
		const newOrder = isAsc ? "desc" : "asc";
		setSorting(true);
		setOrder(newOrder);
		setOrderBy(property);
		// Notify parent component of sort change
		if (onSortChange) {
			onSortChange(newOrder, property);
		}
	};

	return (
		<>
			<Backdrop open={sorting} sx={{ zIndex: 1000, color: "common.white" }}>
				<CircularProgress size="10rem" />
			</Backdrop>
			<TableContainer id={`${id}-table-container`} component={Paper}>
				<Table
					id={`${id}-assignments-table`}
					sx={{ minWidth: 650 }}
					aria-label="simple table"
				>
					<TableHead>
						<TableRow>
							<CustomTableCell
								label="Id"
								property="id"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							<CustomTableCell
								label="Name"
								property="name"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							<CustomTableCell
								label="Description"
								property="description"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							{isRecruitingCompany && (
								<CustomTableCell
									label="Company"
									property="company_name"
									orderBy={orderBy}
									order={order}
									handleRequestSort={handleRequestSort}
								/>
							)}
							<CustomTableCell
								label="Status"
								property="status"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							<CustomTableCell
								label="Votes"
								property="votes"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							<CustomTableCell
								label="Created"
								property="created"
								orderBy={orderBy}
								order={order}
								handleRequestSort={handleRequestSort}
							/>
							{isAdmin && <TableCell>LLM Status</TableCell>}
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedAssignments.map((assignment, index) => (
							<TableRow
								id={`${id}-assignment-row-${assignment.id}`}
								key={assignment.id}
								sx={{
									"&:last-child td, &:last-child th": {
										border: 0,
									},
								}}
							>
								<TableCell component="th" scope="row">
									{assignment.id}
								</TableCell>
								<TableCell>
									{/* Row-wise so the wordmark shares the name's line: at 18px it
									    sits inside the 16px text's line box, so the row keeps its
									    height. */}
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
									>
										<Link
											component="button"
											id={`${id}-assignment-name-link-${assignment.id}`}
											onClick={() =>
												onClickAssignment(assignment)
											}
											sx={{
												cursor: "pointer",
												textAlign: "left",
											}}
										>
											<Typography
												component="span"
												sx={{
													fontSize: 16,
													color: (theme) =>
														theme.palette.text.primary,
												}}
											>
												{assignment.name}
											</Typography>
										</Link>
										{jobylonByAssignment.has(assignment.id) && (
											<JobylonBadge
												height={18}
												href={
													jobylonByAssignment.get(
														assignment.id
													)?.board_url
												}
												title="This role's board is in Jobylon — open it there"
											/>
										)}
									</Stack>
								</TableCell>
								<TableCell>
									<Typography
										variant="body2"
										color="text.secondary"
										maxWidth={300}
										noWrap
									>
										{assignment.description}
									</Typography>
								</TableCell>
								{isRecruitingCompany && (
									<TableCell>
										<Chip
											id={`${id}-assignment-company-chip-${assignment.id}`}
											variant="outlined"
											color="default"
											size="small"
											label={assignment.company_name}
										/>
									</TableCell>
								)}
								<TableCell>
									<Stack
										direction="row"
										spacing={0.5}
										alignItems="center"
									>
										<Chip
											id={`${id}-assignment-status-chip-${assignment.id}`}
											size="small"
											sx={{
												backgroundColor:
													theme.assignmentStatus[
														statusToThemeKey[
															AssignmentStatus[
																assignment.status
															]
														]
													],
												color: "common.black",
											}}
											label={assignment.status_display}
										/>
										{assignment.egenkonsult && (
											<Chip
												id={`${id}-assignment-freelancer-chip-${assignment.id}`}
												data-component="assignment-freelancer-chip"
												size="small"
												color="secondary"
												variant="filled"
												label="Freelancer"
											/>
										)}
									</Stack>
								</TableCell>
								<TableCell>
									{typeof assignment.votes === "number"
										? assignment.votes
										: "-"}
								</TableCell>
								<TableCell>
									{assignment.created
										? formatDateTimeWithSeconds(assignment.created)
										: "-"}
								</TableCell>
								{isAdmin && (
									<TableCell>
										<div
											style={{
												display: "flex",
												gap: "4px",
												flexWrap: "wrap",
											}}
										>
											<Chip
												label={
													assignment.has_manual_requirements
														? "Manual Requirements"
														: "No Manual"
												}
												color={
													assignment.has_manual_requirements
														? "primary"
														: "default"
												}
												size="small"
											/>
										</div>
									</TableCell>
								)}
								<TableCell>
									<MenuActions
										id={`${id}-assignment-row-actions-${assignment.id}`}
										menuActions={[
											// A Jobylon role's candidates live on the customer's
											// own board, so that is where this leads.
											jobylonByAssignment.has(assignment.id)
												? {
														callback: () =>
															window.open(
																jobylonByAssignment.get(
																	assignment.id
																)?.board_url,
																"_blank",
																"noopener"
															),
														text: (
															<ListItemText primary="Go to Jobylon" />
														),
														icon: <LaunchIcon />,
														showDialog: false,
													}
												: {
														callback: () =>
															onGoToBoard(assignment),
														text: (
															<ListItemText primary="Go to board" />
														),
														icon: <LaunchIcon />,
														showDialog: false,
													},
											{
												callback: () =>
													onCloneAssignment(assignment),
												text: <ListItemText primary="Clone" />,
												icon: <FileCopyIcon />,
												showDialog: true,
												dialogTitle:
													"Do you want to clone this role? (everything, including board config will be cloned)",
											},
											{
												callback: () =>
													onDeleteAssignment(assignment),
												text: (
													<ListItemText
														primary="Delete"
														sx={{
															color: (theme) =>
																theme.palette.error
																	.main,
														}}
													/>
												),
												icon: <DeleteForeverIcon />,
												showDialog: true,
												dialogTitle:
													"Do you want to delete this role?",
											},
										]}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

export default AssignmentsList;
